import type { ReactNode } from 'react';
import { readStoredLanguage } from '../../i18n/locale';
import { getRuntimeTimezone } from '../../i18n/preferences';

export function hashNumber(name: string): number | null {
  const query = window.location.hash.split('?')[1] ?? '';
  const value = Number(new URLSearchParams(query).get(name));
  return Number.isInteger(value) && value > 0 ? value : null;
}

export function money(value: number | null | undefined, currency = 'RUB') {
  if (value == null) return '';
  return new Intl.NumberFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value / 100);
}

export function shortDate(value: string | null | undefined) {
  if (!value) return '';
  const normalized = value.includes('T') ? value : `${value}T00:00:00`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function dateTime(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getRuntimeTimezone(),
  }).format(date);
}

export function timeOnly(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getRuntimeTimezone(),
  }).format(date);
}

export function relativeTime(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const delta = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(delta / 60000));
  if (minutes < 1) return 'сейчас';
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'вчера' : `${days} дн назад`;
}

export function initials(value: string) {
  return value.trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]?.toUpperCase()).join('');
}

export function maskPhone(value: string | null | undefined) {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length < 4) return value;
  return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ***-**-${digits.slice(-2)}`;
}

import { CabinetHeading, type CabinetHeadingProps } from '../../ui';

export const CustomerPageHeading = CabinetHeading;
export type CustomerPageHeadingProps = CabinetHeadingProps;

export function customerStatusLabel(status: string) {
  const labels: Record<string, string> = {
    accepted: 'Принята',
    cancelled: 'Отменена',
    closed: 'Завершена',
    completed: 'Завершена',
    delivered: 'Доставлена',
    disputed: 'Спор',
    in_progress: 'В пути',
    matched: 'Перевозчик выбран',
    open: 'Открыт',
    planned: 'Запланирована',
    published: 'Открыта',
    rejected: 'Отклонена',
    resolved: 'Решён',
    sent: 'Отправлен',
  };
  return labels[status] ?? status;
}

export function CustomerStatus({ status }: { status: string }) {
  return <span className={`customer-status is-${status}`} data-status={status}>{customerStatusLabel(status)}</span>;
}

export function transportsCountLabel(count: number, language: string = 'ru'): string {
  if (language === 'en') {
    return `${count} ${count === 1 ? 'transport' : 'transports'}`;
  }
  const rem100 = Math.abs(count) % 100;
  const rem10 = rem100 % 10;
  if (rem100 >= 11 && rem100 <= 19) return `${count}\u00A0перевозок`;
  if (rem10 === 1) return `${count}\u00A0перевозка`;
  if (rem10 >= 2 && rem10 <= 4) return `${count}\u00A0перевозки`;
  return `${count}\u00A0перевозок`;
}

export function formatRatingScore(rating: number | string | null | undefined): string {
  if (rating == null || rating === '') return '0';
  const num = Number(rating);
  if (Number.isNaN(num) || num === 0) return '0';
  return num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
}
